import { refreshReviewToken } from '@/api/widget/reviewsAuth'
import { SafeFetchResponse } from '@/api'
import { ReviewAuthTokenResponse } from '@/types/widget'
import { useSessionStore } from '@/stores/useSessionStore'
import { logger } from '@/utils'

/**
 * Heuristic for detecting a token / auth failure in the collapsed error
 * string returned by `safeFetchAPI`. The widgetapi handler returns
 * "review authorization token is required" / "does not match" / 401 status
 * text depending on the failure mode; match on any of those signals.
 */
function looksLikeAuthError(error: string): boolean {
  return /unauthorized|401|authorization token|does not match|expired|invalid.*token/i.test(error)
}

type RefreshResult = SafeFetchResponse<ReviewAuthTokenResponse>

let refreshInFlight: Promise<RefreshResult> | undefined

async function refreshShared(token: string, pii: string): Promise<RefreshResult> {
  if (refreshInFlight) return refreshInFlight

  refreshInFlight = (async (): Promise<RefreshResult> => {
    const [refreshed, refreshError] = await refreshReviewToken(token)
    if (refreshError || !refreshed?.token || !refreshed?.expiresAt) {
      // A sibling call may have already stored a newer token. Only drop
      // the session if we would be clearing the same token we just tried.
      if (useSessionStore.getState().reviewToken === token) {
        logger.warn('withRefreshRetry: refresh failed, clearing session', {
          refreshError,
        })
        useSessionStore.getState().clearReviewAuth()
      }
      return [undefined, refreshError ?? 'refresh failed']
    }

    useSessionStore.getState().setReviewAuth({
      token: refreshed.token,
      expiresAt: refreshed.expiresAt,
      pii,
    })
    return [refreshed, undefined]
  })().finally(() => {
    refreshInFlight = undefined
  })

  return refreshInFlight
}

/**
 * Run a review-endpoint call with automatic one-shot refresh on auth failure.
 *
 * Flow:
 *   1. Read token from session; if missing, bail with "not authenticated".
 *   2. Call `run(token)`. If success or non-auth error, return.
 *   3. On auth error, share a single in-flight refresh (pending + completed
 *      fetches both go through here). Retry with the store's latest token.
 *   4. On refresh failure, clear session auth only if the token is still
 *      the one we tried to refresh.
 *
 * The caller is expected to route the shopper back to `ReviewSignInPage`
 * when the store's `reviewToken` becomes undefined.
 */
export async function withRefreshRetry<T>(
  run: (token: string) => Promise<SafeFetchResponse<T>>
): Promise<SafeFetchResponse<T>> {
  const { reviewToken, reviewPII } = useSessionStore.getState()
  if (!reviewToken || !reviewPII) {
    return [undefined, 'not authenticated']
  }

  const first = await run(reviewToken)
  const [firstData, firstError] = first
  if (!firstError || firstData !== undefined) return first
  if (!looksLikeAuthError(firstError)) return first

  const latest = useSessionStore.getState()
  if (latest.reviewToken && latest.reviewToken !== reviewToken) {
    return run(latest.reviewToken)
  }
  if (!latest.reviewToken || !latest.reviewPII) {
    return [undefined, firstError]
  }

  logger.log('withRefreshRetry: attempting token refresh', { firstError })

  const [refreshed, refreshError] = await refreshShared(reviewToken, latest.reviewPII)
  if (refreshError || !refreshed?.token) {
    return [undefined, refreshError ?? firstError]
  }
  return run(refreshed.token)
}
